import { DotLoader } from '@ui/DotLoader';
import { Button as UiButton } from '@ui/buttons';
import { ChatComposer } from '@ui/ChatComposer';
import { ChatComposerMenu } from './ChatComposerMenu';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  IconButton,
  InputBase,
  Portal,
  Typography,
} from '@mui/material';
import { ArrowDownward, Close, ExpandMore } from '@mui/icons-material';
import { makeStyles } from 'tss-react/mui';
import { useLocalStorage } from 'usehooks-ts';
import { useNavigate } from 'react-router-dom';
import {
  useSelectedOrganizationName,
  useFullOrganizationByName,
} from '@client/store/organization/organization.hook';
import { useProjectState } from '@client/store/project';
import { useUser } from '@client/hooks/use-user.hooks';
import { requireCreatedProjectDesignPath } from '@shared/helpers/router.helpers';
import { getIsAiChatApproved } from '@shared/helpers/organization.helpers';
import { AI_CHAT_TITLE_MAX_LENGTH } from '@shared/models/ai-chat.model';
import type {
  AiChatAction,
  AiConversationSummary,
} from '@shared/models/ai-chat.model';
import type {
  ProjectImportTaskView,
  ProjectImportThread,
} from '@shared/models/project-import-task.model';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import {
  collectProjectFilesFromFileList,
  type ProjectFileClientUpload,
} from '@client/helpers/project-file-upload-normalize.helpers';
import {
  ChatWindow,
  ChatWindowHeader,
  ChatWindowContent,
} from '@ui/ChatWindow';
import { DropZoneOverlay } from '@features/csv-import/DropZoneOverlay';
import { useFfuFileDrop } from '@features/projects/FfuImport/use-ffu-file-drop.hook';
import { ensureDesignSessionForProject } from '@features/design/store/design.store';
import { useSetDesignRightPanelTab } from '@features/design/store/design-store.hooks';
import { useExpandDesignRightPanel } from '@features/design/design-right-panel-resize.hook';
import { useAiChat } from './use-ai-chat.hooks';
import { useAiChatStore, bucketKey } from './ai-chat.store';
import { aiChatApi } from './ai-chat.api';
import { AgentChatMessage } from './AgentChatMessage';
import {
  formatChatThreadTimestamp,
  shouldShowChatTimestamp,
} from './ai-chat-timestamp.helpers';
import { projectImportTasksApi } from './project-import-tasks.api';

const DEFAULT_SIZE = { width: 440, height: 680 };
const useStyles = makeStyles()((theme) => ({
  launcher: {
    position: 'fixed',
    bottom: 10,
    right: 10,
    zIndex: theme.zIndex.modal,
    width: 320,
    height: 'auto',
    backgroundColor: 'transparent',
    border: 0,
    borderRadius: 0,
    boxShadow: 'none',
    overflow: 'visible',
    maxWidth: 'calc(100vw - 20px)',
  },
  window: { zIndex: theme.zIndex.modal, backgroundColor: '#F7F5F1' },
  title: {
    fontSize: 11,
    fontWeight: 600,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  titleControl: { flex: '1 1 0', minWidth: 0, marginRight: 8 },
  titleInput: {
    width: '100%',
    fontSize: 11,
    fontWeight: 600,
    '& input': { padding: '2px 0' },
  },
  controls: { display: 'flex', flexShrink: 0 },
  icon: { padding: 4, opacity: 0.5 },
  user: {
    width: 'fit-content',
    maxWidth: 'min(85%, calc(100% - 52px))',
    margin: '12px 16px 12px auto',
    padding: '8px 12px',
    borderRadius: '12px 12px 2px 12px',
    background: 'rgba(40,42,49,0.8)',
    color: '#fff',
    fontSize: 13,
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
  },
  task: {
    margin: '8px 20px',
    padding: 12,
    border: '1px solid rgba(0,0,0,0.12)',
    borderRadius: 12,
    fontSize: 13,
    lineHeight: '20px',
  },
  taskTitle: { fontSize: 13, fontWeight: 600 },
  actions: { display: 'flex', flexWrap: 'wrap', gap: 16, paddingBlock: 16 },
  lastActions: { paddingBottom: 0 },
  analysisToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    width: '100%',
    marginTop: 8,
    padding: 0,
    border: 0,
    background: 'none',
    color: 'rgba(0,0,0,0.55)',
    textAlign: 'left',
    font: 'inherit',
    fontSize: 12,
    lineHeight: '18px',
    cursor: 'pointer',
  },
  analysisCaret: { flexShrink: 0, fontSize: 18 },
  activeAnalysis: { color: 'rgba(0,0,0,0.8)' },
  messageActions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 16,
    padding: '16px 20px',
  },
  footer: { flexShrink: 0, padding: '3px 5px 5px' },
  muted: { fontSize: 12, color: 'rgba(0,0,0,0.55)' },
  empty: { padding: '24px 20px', fontSize: 13, color: 'rgba(0,0,0,0.55)' },
  details: {
    marginTop: 0,
    paddingLeft: 22,
    color: 'rgba(0,0,0,0.55)',
    '& p': { fontSize: 12, margin: '4px 0' },
  },
  conversation: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 0',
    minHeight: 0,
  },
  jump: {
    opacity: 0.85,
    position: 'absolute',
    bottom: 8,
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 1,
    borderRadius: 4,
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    paddingBottom: 32,
    overflowAnchor: 'none',
  },
}));

const TaskCard: React.FC<{
  task: ProjectImportTaskView;
  busy: boolean;
  onStart: () => void;
  onDiscard: () => void;
}> = ({ task, busy, onStart, onDiscard }) => {
  const { classes, cx } = useStyles();
  const [details, setDetails] = useState(false);
  const count = task.attachments.length;
  const latestMessage = task.progress?.messages.at(-1);
  const visibleMessage = details ? task.progress?.messages[0] : latestMessage;
  const inProgress = task.state === 'starting' || task.state === 'running';
  const progressIndicator = inProgress && (
    <span role="status" aria-label="Analysis in progress">
      <DotLoader />
    </span>
  );
  let title: string;
  switch (task.state) {
    case 'uploaded':
      title = `${count} ${count === 1 ? 'file' : 'files'} ready`;
      break;
    case 'starting':
      title = 'Starting analysis…';
      break;
    case 'running':
      title = task.progress?.step?.text ?? 'Analysing files…';
      break;
    case 'completed':
      title = 'Project created';
      break;
    case 'failed':
      title = 'Could not create the project';
      break;
    case 'discarded':
      return null;
    default:
      return assertUnreachable(task.state);
  }
  return (
    <Box className={classes.task}>
      <Typography className={classes.taskTitle}>{title}</Typography>
      <Typography className={classes.muted}>
        {task.attachments.map((file) => file.name).join(', ')}
      </Typography>
      {task.error && (
        <Typography className={classes.muted} sx={{ whiteSpace: 'pre-line' }}>
          {task.error}
        </Typography>
      )}
      {(task.state === 'uploaded' || task.state === 'failed') && (
        <Box className={cx(classes.actions, classes.lastActions)}>
          <Button
            size="small"
            variant="contained"
            disabled={busy}
            onClick={onStart}
          >
            {task.state === 'failed' ? 'Try again' : 'Create project'}
          </Button>
          <Button size="small" disabled={busy} onClick={onDiscard}>
            Remove
          </Button>
        </Box>
      )}
      {latestMessage && (
        <Box
          component="button"
          type="button"
          className={cx(
            classes.analysisToggle,
            inProgress &&
              visibleMessage?.id === latestMessage?.id &&
              classes.activeAnalysis,
          )}
          aria-label={details ? 'Collapse analysis' : 'Expand analysis'}
          aria-expanded={details}
          onClick={() => setDetails(!details)}
        >
          <ExpandMore
            className={classes.analysisCaret}
            sx={{ transform: details ? undefined : 'rotate(-90deg)' }}
          />
          <span>
            {visibleMessage?.text}
            {visibleMessage?.id === latestMessage.id && progressIndicator}
          </span>
        </Box>
      )}
      {details && (
        <Box className={classes.details}>
          {task.progress?.messages.slice(1).map((message) => (
            <Typography
              key={message.id}
              className={cx(
                inProgress &&
                  message.id === latestMessage?.id &&
                  classes.activeAnalysis,
              )}
            >
              {message.text}
              {message.id === latestMessage?.id && progressIndicator}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
};

export const OrganizationAgentChat: React.FC = () => {
  const organization = useSelectedOrganizationName();
  const fullOrganization = useFullOrganizationByName(organization);
  const user = useUser();
  if (!organization || !user || !getIsAiChatApproved(fullOrganization))
    return null;
  return (
    <OrganizationAgentThread
      key={`${organization}:${user.id}`}
      organization={organization}
      userId={user.id}
    />
  );
};

const OrganizationAgentThread: React.FC<{
  organization: string;
  userId: string;
}> = ({ organization, userId }) => {
  const { classes, cx } = useStyles();
  const chat = useAiChat(organization, null);
  const conversation = chat.status === 'ready' ? chat.conversation : undefined;
  const conversationId = conversation?.id;
  const navigate = useNavigate();
  const { fetchProject, fetchProjects } = useProjectState(
    'fetchProject',
    'fetchProjects',
  );
  const setPanel = useSetDesignRightPanelTab();
  const expandPanel = useExpandDesignRightPanel();
  const storageKey = `nodon:organization-agent:${organization}:${userId}`;
  const [open, setOpen] = useLocalStorage(`${storageKey}:open`, false);
  const [size, setSize] = useLocalStorage(`${storageKey}:size`, DEFAULT_SIZE);
  const [selectedId, setSelectedId] = useLocalStorage<string | null>(
    `${storageKey}:conversation`,
    null,
  );
  const [draft, setDraft] = useLocalStorage(
    `${storageKey}:draft:${conversationId ?? 'loading'}`,
    '',
  );
  const [tasks, setTasks] = useState<ProjectImportTaskView[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'upload' | 'task' | null>(null);
  const [history, setHistory] = useState<AiConversationSummary[]>([]);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const savingTitle = useRef(false);
  useEffect(() => setEditingTitle(false), [conversationId]);
  const saveTitle = async () => {
    if (!conversationId || savingTitle.current) return;
    const title = titleDraft.trim();
    if (!title || title === conversation?.title) {
      setEditingTitle(false);
      return;
    }
    savingTitle.current = true;
    try {
      await aiChatApi.patchConversationTitle(conversationId, { title });
      const current =
        useAiChatStore.getState().byKey[bucketKey(organization, null)];
      if (
        current?.status === 'ready' &&
        current.conversation.id === conversationId
      ) {
        useAiChatStore.getState().setConversation(organization, null, {
          ...current.conversation,
          title,
        });
        setEditingTitle(false);
      }
      setHistory((rows) =>
        rows.map((row) =>
          row.id === conversationId ? { ...row, preview: title } : row,
        ),
      );
    } catch {
      setError('Could not rename the conversation. Try again.');
    } finally {
      savingTitle.current = false;
    }
  };
  const input = useRef<HTMLInputElement>(null);
  const launcher = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const dock = launcher.current;
    if (open || !dock) return;
    const property = '--organization-agent-dock-top';
    const update = () =>
      document.documentElement.style.setProperty(
        property,
        `${dock.getBoundingClientRect().top}px`,
      );
    update();
    const observer = new ResizeObserver(update);
    observer.observe(dock);
    window.addEventListener('resize', update);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
      document.documentElement.style.removeProperty(property);
    };
  }, [open]);
  const composerInput = useRef<HTMLInputElement>(null);
  const focusOnOpen = useRef(false);
  useLayoutEffect(() => {
    if (!open || !focusOnOpen.current || chat.status !== 'ready') return;
    const field = composerInput.current;
    if (!field) return;
    field.focus();
    field.setSelectionRange(field.value.length, field.value.length);
    focusOnOpen.current = false;
  }, [open, chat.status]);
  const content = useRef<HTMLDivElement>(null);
  const [feedNode, setFeedNode] = useState<HTMLDivElement | null>(null);
  const following = useRef(true);
  const turnSpacer = useRef<HTMLDivElement>(null);
  const questionAnchor = useRef<string | null>(null);
  const questionPinned = useRef(false);
  const pendingQuestion = useRef<{ previousId: string | undefined } | null>(
    null,
  );
  const [newMessages, setNewMessages] = useState(false);
  const restored = useRef(false);
  const running = tasks.some(
    (task) => task.state === 'starting' || task.state === 'running',
  );
  const pending = useRef(new Set<string>());
  const activeId = useRef(conversationId);
  activeId.current = conversationId;
  const actionBoundary = useRef<Set<string> | null>(null);

  const openProject = useCallback(
    async (projectId: number) => {
      const project = await fetchProject(projectId);
      if (!project) throw new Error('Could not open the project.');
      ensureDesignSessionForProject(projectId);
      setPanel('agent');
      expandPanel();
      navigate(
        requireCreatedProjectDesignPath(
          organization,
          projectId,
          project.active_version_id,
        ),
      );
    },
    [fetchProject, setPanel, expandPanel, navigate, organization],
  );

  const apply = useCallback(
    (thread: ProjectImportThread) => {
      if (activeId.current !== thread.conversation.id) return;
      setError((current) =>
        current === 'Could not load agent history. Try again.' ? null : current,
      );
      setTasks(thread.tasks);
      useAiChatStore.getState().upsertConversation(thread.conversation);
      for (const task of thread.tasks) {
        if (task.state === 'starting' || task.state === 'running')
          pending.current.add(task.id);
        if (task.state === 'completed' && pending.current.delete(task.id))
          void fetchProjects();
      }
    },
    [fetchProjects],
  );

  useEffect(() => {
    if (!conversationId || restored.current) return;
    restored.current = true;
    if (!selectedId || selectedId === conversationId) {
      setSelectedId(conversationId);
      return;
    }
    void aiChatApi
      .getConversation(selectedId)
      .then((saved) => {
        if (
          activeId.current === conversationId &&
          saved.organizationId === organization &&
          saved.projectId === null
        )
          useAiChatStore.getState().setConversation(organization, null, saved);
      })
      .catch(() => setSelectedId(conversationId));
  }, [conversationId, selectedId, organization, setSelectedId]);

  useEffect(() => {
    if (!conversationId) return;
    let active = true;
    const read = () =>
      projectImportTasksApi
        .read(conversationId)
        .then((thread) => {
          if (active) apply(thread);
        })
        .catch(() => {
          if (active) setError('Could not load agent history. Try again.');
        });
    void read();
    const timer = window.setInterval(() => void read(), running ? 3000 : 15000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [conversationId, apply, running]);

  useLayoutEffect(() => {
    if (!open || !feedNode) return;
    const follow = () => {
      const root = content.current;
      const spacer = turnSpacer.current;
      if (!root || !spacer) return;
      const userRows = Array.from(
        feedNode.querySelectorAll<HTMLElement>('[data-chat-user-message]'),
      );
      const latest = userRows.at(-1);
      let alignQuestion = false;
      if (pendingQuestion.current) {
        if (
          !latest ||
          latest.dataset.chatUserMessage === pendingQuestion.current.previousId
        )
          return;
        questionAnchor.current = latest.dataset.chatUserMessage ?? null;
        pendingQuestion.current = null;
        following.current = false;
        alignQuestion = true;
        questionPinned.current = true;
      }
      const anchor = userRows.find(
        (row) => row.dataset.chatUserMessage === questionAnchor.current,
      );
      if (anchor) {
        // Reserve the rest of this turn's viewport before the answer arrives.
        // Shrink the space as the answer grows, keeping the question in place.
        const turnHeight =
          feedNode.getBoundingClientRect().bottom -
          anchor.getBoundingClientRect().top -
          spacer.getBoundingClientRect().height;
        const needed = Math.max(0, root.clientHeight - 8 - 32 - turnHeight);
        if (Math.abs(spacer.getBoundingClientRect().height - needed) > 0.5)
          spacer.style.height = `${needed}px`;
        if (alignQuestion || questionPinned.current) {
          root.scrollTop +=
            anchor.getBoundingClientRect().top -
            root.getBoundingClientRect().top -
            8;
          setNewMessages(
            root.scrollHeight - root.scrollTop - root.clientHeight > 32,
          );
          return;
        }
      } else {
        spacer.style.height = '0px';
      }
      if (following.current && !anchor) root.scrollTop = root.scrollHeight;
      else
        setNewMessages(
          root.scrollHeight - root.scrollTop - root.clientHeight > 32,
        );
    };
    const observer = new ResizeObserver(follow);
    observer.observe(feedNode);
    if (content.current) observer.observe(content.current);
    follow();
    return () => observer.disconnect();
  }, [open, conversationId, feedNode, conversation?.messages]);

  useEffect(() => {
    if (!conversation || !actionBoundary.current) return;
    const action = conversation.messages
      .filter((message) => !actionBoundary.current?.has(message.id))
      .flatMap((message) => message.toolCalls ?? [])
      .map((tool) => tool.result?.action)
      .find((value): value is AiChatAction => !!value);
    const finishedReply =
      conversation.status === 'idle' &&
      conversation.messages.some(
        (message) =>
          !actionBoundary.current?.has(message.id) &&
          message.role === 'assistant' &&
          message.status === 'processed' &&
          !message.toolCalls?.length,
      );
    if (finishedReply || action) actionBoundary.current = null;
    if (action) {
      void openProject(action.projectId).catch(() =>
        setError('Could not open the project. Try again.'),
      );
    }
  }, [conversation, openProject]);

  useEffect(() => {
    actionBoundary.current = null;
  }, [conversationId]);

  const upload = async (files: ProjectFileClientUpload[]) => {
    setOpen(true);
    if (busy) {
      setError('Please wait until the current upload finishes.');
      return;
    }
    if (!conversationId || !files.length) return;
    setBusy('upload');
    setError(null);
    try {
      apply(await projectImportTasksApi.upload(conversationId, files));
    } catch {
      setError('Could not upload the files. Try again.');
    } finally {
      setBusy(null);
    }
  };
  const dragging = useFfuFileDrop({
    enabled: !!conversationId,
    onFiles: (files) => void upload(files),
    onError: () => {
      setOpen(true);
      setError('Could not read the files.');
    },
  });
  const actOnTask = async (
    task: ProjectImportTaskView,
    action: 'start' | 'discard',
  ) => {
    if (!conversationId || busy) return;
    setBusy('task');
    setError(null);
    try {
      apply(await projectImportTasksApi[action](conversationId, task.id));
    } catch {
      setError('Could not update the analysis task. Try again.');
    } finally {
      setBusy(null);
    }
  };
  const send = async () => {
    if (chat.status !== 'ready' || chat.isSending || !draft.trim()) return;
    setOpen(true);
    pendingQuestion.current = {
      previousId: chat.conversation.messages.findLast(
        (message) => message.role === 'user',
      )?.id,
    };
    following.current = false;
    setNewMessages(false);
    actionBoundary.current = new Set(
      chat.conversation.messages.map((message) => message.id),
    );
    const text = draft.trim();
    setDraft('');
    setError(null);
    try {
      await chat.sendMessage(text, { scope: 'organization' });
      const current =
        useAiChatStore.getState().byKey[bucketKey(organization, null)];
      if (current?.status === 'ready' && current.sendError) {
        setDraft(text);
        setError('Could not send the message. Try again.');
        pendingQuestion.current = null;
        actionBoundary.current = null;
      }
    } catch {
      pendingQuestion.current = null;
      setDraft(text);
      setError('Could not send the message. Try again.');
    }
  };
  const showHistory = async () => {
    try {
      setHistory(await aiChatApi.listConversations(null));
    } catch {
      setError('Could not load conversation history.');
    }
  };
  const selectThread = async (id: string) => {
    actionBoundary.current = null;
    const saved = await aiChatApi.getConversation(id);
    setSelectedId(id);
    setOpen(true);
    setTasks([]);
    following.current = true;
    questionAnchor.current = null;
    pendingQuestion.current = null;
    setNewMessages(false);
    useAiChatStore.getState().setConversation(organization, null, saved);
  };
  const newChat = async () => {
    if (chat.status !== 'ready') return;
    actionBoundary.current = null;
    await chat.createNewConversation();
    const current =
      useAiChatStore.getState().byKey[bucketKey(organization, null)];
    if (current?.status === 'ready') setSelectedId(current.conversation.id);
    following.current = true;
    questionAnchor.current = null;
    pendingQuestion.current = null;
    setNewMessages(false);
  };
  let activityText = '';
  if (busy === 'upload') activityText = 'Uploading…';
  else if (busy === 'task') activityText = 'Updating analysis…';
  const thinking =
    chat.status === 'ready' &&
    (chat.isSending || chat.conversation.status === 'processing');
  const thinkingIndicator = (
    <span role="status" aria-label="Thinking">
      Thinking
      <DotLoader />
    </span>
  );
  const messages = (conversation?.messages ?? []).filter(
    (message) => message.content.trim() || message.status !== 'processed',
  );
  const cards = (messageId: string) =>
    tasks
      .filter((task) => task.sourceMessageId === messageId)
      .map((task) => (
        <TaskCard
          key={task.id}
          task={task}
          busy={!!busy}
          onStart={() => void actOnTask(task, 'start')}
          onDiscard={() => void actOnTask(task, 'discard')}
        />
      ));

  const composer = (
    <ChatComposer
      appearance={open ? 'panel' : 'floating'}
      inputRef={composerInput}
      onOpenConversation={
        open
          ? undefined
          : () => {
              focusOnOpen.current = true;
              setOpen(true);
            }
      }
      value={draft}
      onChange={setDraft}
      placeholder="Ask the agent..."
      disabled={chat.status !== 'ready'}
      onKeyDown={(event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault();
          void send();
        }
      }}
      canSend={chat.status === 'ready' && !chat.isSending && !!draft.trim()}
      onSend={() => void send()}
      onUploadClick={() => input.current?.click()}
      uploadDisabled={!!busy || !conversationId}
      menu={
        <ChatComposerMenu
          history={history}
          historyLoading={false}
          activeConversationId={conversationId}
          newChatDisabled={chat.status !== 'ready' || chat.isSending}
          onFetchHistory={() => void showHistory()}
          onSelectHistory={(id) =>
            void selectThread(id).catch(() =>
              setError('Could not open the conversation.'),
            )
          }
          onNewChat={() =>
            void newChat().catch(() => setError('Could not create a new chat.'))
          }
        />
      }
    />
  );

  return (
    <>
      <DropZoneOverlay
        state={
          dragging ? { kind: 'dragging', validity: 'valid' } : { kind: 'idle' }
        }
        labels={{
          neutralTitle: 'Drop files to upload',
          neutralSubtitle:
            'Choose what to do with your files in the agent chat',
        }}
      />
      <Button size="small" onClick={() => setOpen(true)}>
        Open agent
      </Button>
      <Portal>
        <Box onClick={(event) => event.stopPropagation()}>
          <ChatWindow
            className={cx(classes.window, !open && classes.launcher)}
            width={open ? size.width : undefined}
            height={open ? size.height : undefined}
            onResize={open ? setSize : undefined}
          >
            {open && (
              <ChatWindowHeader>
                <Box className={classes.titleControl} data-chat-header-control>
                  {editingTitle ? (
                    <InputBase
                      autoFocus
                      className={classes.titleInput}
                      value={titleDraft}
                      inputProps={{
                        'aria-label': 'Conversation name',
                        maxLength: AI_CHAT_TITLE_MAX_LENGTH,
                      }}
                      onFocus={(event) => event.target.select()}
                      onChange={(event) => setTitleDraft(event.target.value)}
                      onBlur={() => void saveTitle()}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          void saveTitle();
                        }
                        if (event.key === 'Escape') {
                          event.preventDefault();
                          setEditingTitle(false);
                        }
                      }}
                    />
                  ) : (
                    <Typography
                      className={classes.title}
                      onDoubleClick={() => {
                        if (!conversationId) return;
                        setTitleDraft(conversation?.title || 'Agent');
                        setEditingTitle(true);
                      }}
                    >
                      {conversation?.title || 'Agent'}
                    </Typography>
                  )}
                </Box>
                <Box className={classes.controls}>
                  <IconButton
                    size="small"
                    className={classes.icon}
                    aria-label="Close agent"
                    data-chat-close
                    onClick={() => setOpen(false)}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
              </ChatWindowHeader>
            )}
            {open && (
              <Box
                className={classes.conversation}
                onWheelCapture={() => {
                  questionPinned.current = false;
                  pendingQuestion.current = null;
                }}
                onTouchMoveCapture={() => {
                  questionPinned.current = false;
                  pendingQuestion.current = null;
                }}
                onPointerDownCapture={(event) => {
                  if (event.target === content.current)
                    questionPinned.current = false;
                }}
                onKeyDownCapture={(event) => {
                  if (
                    event.target === content.current &&
                    [
                      'ArrowUp',
                      'ArrowDown',
                      'PageUp',
                      'PageDown',
                      'Home',
                      'End',
                      ' ',
                    ].includes(event.key)
                  ) {
                    questionPinned.current = false;
                    pendingQuestion.current = null;
                  }
                }}
              >
                <ChatWindowContent
                  scrollRef={content}
                  className={classes.body}
                  onScroll={(event) => {
                    const node = event.currentTarget;
                    following.current =
                      node.scrollHeight - node.scrollTop - node.clientHeight <
                      32;
                    if (following.current) {
                      setNewMessages(false);
                    }
                  }}
                >
                  <Box ref={setFeedNode}>
                    {!messages.length && (
                      <Typography className={classes.empty}>
                        {chat.status === 'error'
                          ? chat.error.message
                          : 'Ask about your projects, or drop files to create a new one.'}
                      </Typography>
                    )}
                    {messages.map((message, index) => (
                      <React.Fragment key={message.id}>
                        {message.role === 'user' ? (
                          <Box
                            className={classes.user}
                            data-chat-user-message={message.id}
                          >
                            {message.content}
                          </Box>
                        ) : (
                          <AgentChatMessage
                            message={message}
                            trailing={
                              message.role === 'assistant' &&
                              message.status === 'pending' &&
                              (message.content.trim() ? (
                                <DotLoader />
                              ) : (
                                thinkingIndicator
                              ))
                            }
                            timestamp={
                              shouldShowChatTimestamp(
                                message.createdAt,
                                messages[index - 1]?.createdAt,
                              )
                                ? formatChatThreadTimestamp(
                                    message.createdAt,
                                    new Date(),
                                  )
                                : undefined
                            }
                          />
                        )}
                        {cards(message.id)}
                        {tasks
                          .filter(
                            (task) =>
                              task.resultMessageId === message.id &&
                              task.projectId !== null,
                          )
                          .map((task) => (
                            <Box
                              key={task.id}
                              className={classes.messageActions}
                            >
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => {
                                  if (task.projectId !== null)
                                    void openProject(task.projectId).catch(() =>
                                      setError('Could not open the project.'),
                                    );
                                }}
                              >
                                Open project
                              </Button>
                            </Box>
                          ))}
                      </React.Fragment>
                    ))}
                    {thinking &&
                      !messages.some(
                        (message) =>
                          message.role === 'assistant' &&
                          message.status === 'pending',
                      ) && (
                        <AgentChatMessage
                          message={{ content: '', createdAt: '' }}
                          trailing={thinkingIndicator}
                        />
                      )}
                    <Box ref={turnSpacer} aria-hidden="true" />
                  </Box>
                </ChatWindowContent>
                {newMessages && (
                  <Box className={classes.jump}>
                    <UiButton
                      variant="contained"
                      size="tiny"
                      minWidth={0}
                      endIcon={<ArrowDownward />}
                      onClick={() => {
                        questionAnchor.current = null;
                        pendingQuestion.current = null;
                        if (turnSpacer.current)
                          turnSpacer.current.style.height = '0px';
                        following.current = true;
                        setNewMessages(false);
                        content.current?.scrollTo({
                          top: content.current.scrollHeight,
                          behavior: 'smooth',
                        });
                      }}
                    >
                      New messages
                    </UiButton>
                  </Box>
                )}
              </Box>
            )}
            <Box ref={launcher} className={open ? classes.footer : undefined}>
              {error && (
                <Typography className={classes.empty} role="alert">
                  {error}
                </Typography>
              )}
              {activityText && (
                <Typography className={classes.muted} role="status">
                  {activityText}
                </Typography>
              )}
              {composer}
            </Box>
          </ChatWindow>
          <input
            ref={input}
            type="file"
            multiple
            hidden
            onChange={(event) => {
              const files = collectProjectFilesFromFileList(
                event.target.files ?? [],
              );
              event.target.value = '';
              void upload(files);
            }}
          />
        </Box>
      </Portal>
    </>
  );
};
